import { classNames } from '@fullstory/util/fmt';
import React, {
  ComponentProps,
  forwardRef,
  useContext,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Box } from '../../legacy/Box';
import { Flex } from '../../legacy/Flex';
import { identifyComponent } from '../../util';
import { Badge } from '../Badge';
import { ListContext } from '../List/context';
import { Text } from '../Text';
import { useLongHover } from './hooks';
import css from './index.module.scss';

export interface ListItemTextProps {
  /**
   * If `true`, the children will be indented. This should be used if
   * there is no left avatar or left icon.
   */
  inset?: boolean;
  /**
   * The main text.
   */
  text: React.ComponentProps<typeof Text>['children'];

  /**
   * The subtext.
   */
  subtext?: React.ComponentProps<typeof Text>['children'];
  /**
   * Set overflow to ellipsis on both text and subtext when it overflows the parent.
   */
  noWrap?: React.ComponentProps<typeof Text>['noWrap'];
  /**
   * If true, the Option will have a smaller font size to support higher density list
   */
  compact?: boolean;
  badgeLabel?: string;
  badgeProps?: Omit<ComponentProps<typeof Badge>, 'children'>;
}

export const ListItemText = forwardRef<HTMLDivElement, ListItemTextProps>(
  (
    { inset = false, text, subtext, noWrap, compact: compactProp, badgeLabel, badgeProps, ...rest },
    ref,
  ) => {
    const listContext = useContext(ListContext);
    const compact = compactProp ?? listContext.compact ?? false;
    const { hovered, onHover, onCancelHover } = useLongHover();
    const [height, setHeight] = useState('auto');
    const textSize: ComponentProps<typeof Text>['appearance'] = compact
      ? 'bodySmall'
      : 'bodyMedium';
    const wrapperEl = useRef<HTMLDivElement>(null);
    const contentEl = useRef<HTMLDivElement>(null);

    // let the `ListItemText` component manage the ref
    useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(ref, () => wrapperEl.current);

    const contentClassName = classNames(css.content, {
      [css.inset]: inset,
    });

    const badgeClassName = classNames(css.badge, {
      [css.compact]: compact,
    });

    // handle expand height animation
    useLayoutEffect(() => {
      if (contentEl.current) {
        setHeight(`${contentEl.current.clientHeight}px`);
      }
    }, [hovered]);

    if (noWrap) {
      return (
        <Box
          data-component="ListItemText"
          ref={wrapperEl}
          onMouseOver={onHover}
          onMouseOut={onCancelHover}
          onFocus={onHover}
          onBlur={onCancelHover}
          width={1}
          height={height}
          overflow="hidden"
          style={{ transition: 'height 200ms' }}
          {...rest}
          {...identifyComponent('list-item-text', rest)}
        >
          <div ref={contentEl} className={contentClassName}>
            <Flex alignItems="flex-start" justifyContent="flex-start">
              <Text noWrap={!hovered} appearance={textSize} tag="span" className={css.text}>
                {text}
              </Text>
              {badgeLabel && (
                <Box marginLeft={8} flexShrink={0} className={badgeClassName}>
                  <Badge {...badgeProps}>{badgeLabel}</Badge>
                </Box>
              )}
            </Flex>
            <Text
              noWrap={!hovered}
              appearance={textSize}
              tag="span"
              className={css.text}
              color="secondary"
            >
              {subtext}
            </Text>
          </div>
        </Box>
      );
    }
    return (
      <div data-component="ListItemText" className={contentClassName}>
        <Flex alignItems="flex-start" justifyContent="flex-start">
          <Text appearance={textSize} tag="span">
            {text}
          </Text>
          {badgeLabel && (
            <Box marginLeft={8} flexShrink={0} className={badgeClassName}>
              <Badge {...badgeProps}>{badgeLabel}</Badge>
            </Box>
          )}
        </Flex>
        <Text appearance={textSize} tag="span" color="secondary">
          {subtext}
        </Text>
      </div>
    );
  },
);

ListItemText.displayName = 'ListItemText';
